import { useState } from 'react';

import { CheckCheck, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { formatRelativeTime } from '@/utils/date-formatters';

import { NOTIFICATION_TYPE_CONFIG } from '../../constants/notification-type-config';
import { Notification } from '../../types';

interface NotificationItemActionProps {
  notification: Notification;
  onMarkAsRead?: (notificationId: number) => void;
  onDelete?: (notificationId: number) => void;
  onAction?: (notificationId: number) => void;
}

/**
 * Notification item with action button.
 * Shows title, description, timestamp, action button, and unread indicator.
 * "Mark as read" and delete buttons appear on hover.
 */
export function NotificationItemAction({
  notification,
  onMarkAsRead,
  onDelete,
  onAction,
}: NotificationItemActionProps) {
  const { t } = useTranslation('authenticated.notifications');
  const [isHovering, setIsHovering] = useState(false);

  const handleMarkAsRead = () => {
    if (!notification.isRead && onMarkAsRead) {
      onMarkAsRead(notification.id);
    }
  };

  const typeConfig = NOTIFICATION_TYPE_CONFIG[notification.type];
  const title = typeConfig ? t(typeConfig.titleKey, notification.args ?? {}) : '';
  const description = typeConfig ? t(typeConfig.descriptionKey, notification.args ?? {}) : '';

  const handleDelete = () => {
    onDelete?.(notification.id);
  };

  const handleAction = () => {
    onAction?.(notification.id);
  };

  return (
    <div
      className={cn(
        'flex flex-col gap-2 border-b border-border px-4 py-3',
        !notification.isRead && 'bg-muted/30'
      )}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div className="flex items-start justify-between gap-2">
        <h4 className="flex-1 text-sm font-medium leading-tight text-foreground">{title}</h4>
        <div className="relative flex h-5 items-center gap-1">
          {isHovering ? (
            <>
              {!notification.isRead && (
                <button
                  type="button"
                  className="flex items-center gap-1.5 rounded-full border bg-background px-2.5 py-1 text-xs text-foreground shadow-sm"
                  onClick={handleMarkAsRead}
                >
                  <CheckCheck className="h-4 w-4" />
                  {t('inbox.markAsRead')}
                </button>
              )}
              <button
                type="button"
                className="flex items-center rounded-md border bg-background p-1 text-muted-foreground shadow-sm hover:text-destructive"
                onClick={handleDelete}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </>
          ) : (
            <>
              <span className="whitespace-nowrap text-xs text-muted-foreground">
                {formatRelativeTime(notification.createdAt)}
              </span>
              {!notification.isRead && (
                <span className="ml-1.5 h-2 w-2 shrink-0 rounded-md bg-destructive" />
              )}
            </>
          )}
        </div>
      </div>
      <p className="text-sm leading-snug text-muted-foreground">{description}</p>
      <Button variant="default" size="sm" className="mt-1 w-fit" onClick={handleAction}>
        {t('inbox.action')}
      </Button>
    </div>
  );
}
